import {
  getNizza,
  NizzaGetCollectionHandler,
  NizzaGetProductHandler,
  NizzaGetProductHandlerInput,
  NizzaGetProductListHandler,
  NizzaGetProductListHandlerInput,
  NizzaProduct,
  NizzaProductDataSource,
  NizzaProductDataSourceConfig,
  nzApiClient,
  VtexProduct,
} from '@nizza/core';
import baseLogger from '~logger';
import { buildVtexCatalogUrl, vtexToNizzaProduct } from '../../vtex';

const logger = baseLogger.child('VtexProductDataSource');

type Config = NizzaProductDataSourceConfig & {
  corsProxy?: boolean;
};

export class VtexProductDataSource implements NizzaProductDataSource {
  private accountName: string;
  private accountHost: string | null;
  private defaultUseProxy = false;

  constructor(readonly config: Config) {
    const nizza = getNizza();
    if (!nizza.account) throw new Error('Nizza account is invalid');
    const { account, host } = nizza.account;

    this.accountName = account;
    this.accountHost = host;
    this.defaultUseProxy = config.corsProxy ?? false;
  }

  getProduct: NizzaGetProductHandler = async input => {
    const data = await this.fetchProducts(input);
    return this.convertProductData(data)[0] || null;
  };

  getProductList: NizzaGetProductListHandler = async input => {
    const data = await this.fetchProducts(input);
    return this.convertProductData(data);
  };

  getCollection: NizzaGetCollectionHandler = async input => {
    const data = await this.fetchProducts(input);
    const products = this.convertProductData(data);

    let collectionName = '';
    const collectionId = input?.collectionId ?? '';

    if (collectionId && data.length > 0) {
      collectionName = data[0]?.productClusters?.[collectionId] ?? '';
    }

    return {
      id: collectionId,
      name: collectionName,
      products,
    };
  };

  private convertProductData(data: VtexProduct[]): NizzaProduct[] {
    const { accountApplyTax } = this.config;
    return data.map(product =>
      vtexToNizzaProduct(product, this.accountHost, accountApplyTax),
    );
  }

  private async fetchProducts(
    input: NizzaGetProductHandlerInput | NizzaGetProductListHandlerInput = {},
  ): Promise<VtexProduct[]> {
    try {
      // The first attempt uses the proxy default configuration
      const { _rawData } = await this.tryFetchProducts(
        input,
        this.defaultUseProxy,
      );
      return _rawData || [];
    } catch (error: any) {
      logger.debug('Error fetching products directly. Retrying with proxy.', {
        originalError: error.message,
      });
      // If the first attempt fails, we retry forcing the proxy.
      return this.retryFetchProductsWithProxy(input, error);
    }
  }

  private async retryFetchProductsWithProxy(
    input: NizzaGetProductHandlerInput | NizzaGetProductListHandlerInput,
    hostError: Error,
  ): Promise<VtexProduct[]> {
    try {
      const { _rawData } = await this.tryFetchProducts(input, true); // forzarProxyTo `true`
      return _rawData || [];
    } catch (proxyError: any) {
      // Both attempts failed.
      const errorMessage = `Failed to fetch products. Host error: ${hostError.message}, Proxy error: ${proxyError.message}`;
      logger.error(errorMessage, { hostError, proxyError });
      // We launched a new mistake that encapsulates both failures.
      throw new Error(errorMessage);
    }
  }

  private async tryFetchProducts(
    input: NizzaGetProductHandlerInput | NizzaGetProductListHandlerInput,
    useProxy: boolean,
  ) {
    const corsProxyOrigin = useProxy
      ? this.defaultUseProxy
        ? 'ls-default-proxy'
        : 'ls-products-cors-error'
      : undefined;

    // `buildVtexCatalogUrl` generates an absolute URL
    const fullUrl = buildVtexCatalogUrl({
      limit: this.config.defaultLimit,
      corsProxy: useProxy,
      corsProxyOrigin,
      account: this.accountName,
      accountHost: this.accountHost,
      ...input,
    });

    // Extract the base and path from the absolute URL to pass to ApiClient
    const urlObject = new URL(fullUrl);
    const baseURL = urlObject.origin;
    const path = urlObject.pathname + urlObject.search + urlObject.hash;

    return nzApiClient.rest<VtexProduct[]>({
      baseURL,
      path,
      timeout: 5000,
    });
  }
}
